import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader2, User, Mail, Lock, GamepadIcon, Phone } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";

interface SignupFormProps {
  nickname: string;
  email: string;
  password: string;
  whatsappNumber: string;
  preferredGame: string;
  role: "mentor" | "jogador";
  loading: boolean;
  onNicknameChange: (val: string) => void;
  onEmailChange: (val: string) => void;
  onPasswordChange: (val: string) => void;
  onWhatsappNumberChange: (val: string) => void;
  onPreferredGameChange: (val: string) => void;
  onRoleChange: (role: "mentor" | "jogador") => void;
  onSubmit: (e: React.FormEvent) => Promise<void> | void;
  onGoogleLogin?: (e: React.MouseEvent) => Promise<void> | void;
}

const SignupForm: React.FC<SignupFormProps> = ({
  nickname,
  email,
  password,
  whatsappNumber,
  preferredGame,
  role,
  loading,
  onNicknameChange,
  onEmailChange,
  onPasswordChange,
  onWhatsappNumberChange,
  onPreferredGameChange,
  onRoleChange,
  onSubmit,
  onGoogleLogin
}) => {
  const [games, setGames] = useState<Array<{ id: string; name: string }>>([]);
  const [gamesLoading, setGamesLoading] = useState(true);

  // Buscar jogos da base de dados
  useEffect(() => {
    const fetchGames = async () => {
      try {
        const { data, error } = await supabase
          .from("games")
          .select("id, name")
          .order("name");

        if (error) {
          console.error("Error fetching games:", error);
          // Fallback para lista padrão em caso de erro
          setGames([
            { id: "fallback-lol", name: "League of Legends" },
            { id: "fallback-valorant", name: "Valorant" },
            { id: "fallback-cs2", name: "CS2" },
            { id: "fallback-outro", name: "Outro" }
          ]);
        } else {
          // Adicionar opção "Outro" no final da lista
          const gamesWithOther = [...(data || []), { id: "outro", name: "Outro" }];
          setGames(gamesWithOther);
        }
      } catch (error) {
        console.error("Error fetching games:", error);
        // Fallback para lista padrão
        setGames([
          { id: "fallback-lol", name: "League of Legends" },
          { id: "fallback-valorant", name: "Valorant" },
          { id: "fallback-cs2", name: "CS2" },
          { id: "fallback-outro", name: "Outro" }
        ]);
      } finally {
        setGamesLoading(false);
      }
    };

    fetchGames();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submission prevented");
    
    if (onSubmit) {
      onSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Nickname Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <User className="h-5 w-5 text-white/40" />
        </div>
        <Input
          autoFocus
          type="text"
          placeholder="Apelido/Nome"
          value={nickname}
          onChange={(e) => onNicknameChange(e.target.value)}
          required
          className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 focus:border-[#00B6F0]/50 focus:ring-1 focus:ring-[#00B6F0]/50"
        />
      </div>
      
      {/* Email Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Mail className="h-5 w-5 text-white/40" />
        </div>
        <Input
          type="email"
          placeholder="Email"
          value={email}
          autoComplete="username"
          onChange={(e) => onEmailChange(e.target.value)}
          required
          className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 focus:border-[#00B6F0]/50 focus:ring-1 focus:ring-[#00B6F0]/50"
        />
      </div>
      
      {/* Password Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Lock className="h-5 w-5 text-white/40" />
        </div>
        <Input
          type="password"
          placeholder="Senha"
          value={password}
          autoComplete="new-password"
          onChange={(e) => onPasswordChange(e.target.value)}
          required
          className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 focus:border-[#00B6F0]/50 focus:ring-1 focus:ring-[#00B6F0]/50"
        />
      </div>
      
      {/* WhatsApp Number Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Phone className="h-5 w-5 text-white/40" />
        </div>
        <Input
          type="tel"
          placeholder="Número de WhatsApp (ex: +5511999999999)"
          value={whatsappNumber}
          onChange={(e) => onWhatsappNumberChange(e.target.value)}
          className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 focus:border-[#00B6F0]/50 focus:ring-1 focus:ring-[#00B6F0]/50"
        />
        <div className="text-xs text-white/60 mt-1">
          Campo opcional - Formato: +55 + DDD + número
        </div>
      </div>
      
      {/* Game Selection */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <GamepadIcon className="h-5 w-5 text-white/40" />
          <label className="text-sm text-white/80">Jogo Preferido</label>
        </div>
        <select
          className="w-full p-2 rounded-md border bg-white/10 text-white border-white/10 focus:border-[#00B6F0]/50 focus:ring-1 focus:ring-[#00B6F0]/50"
          value={preferredGame}
          onChange={(e) => onPreferredGameChange(e.target.value)}
          required
          disabled={gamesLoading}
        >
          {gamesLoading ? (
            <option value="" className="bg-gray-800 text-white">Carregando jogos...</option>
          ) : (
            games.map((game) => (
              <option key={game.id} value={game.name} className="bg-gray-800 text-white">{game.name}</option>
            ))
          )}
        </select>
        {gamesLoading && (
          <div className="flex items-center gap-2 mt-1">
            <Loader2 className="h-3 w-3 text-white/40 animate-spin" />
            <span className="text-xs text-white/40">Carregando jogos...</span>
          </div>
        )}
      </div>
      
      {/* Account Type Selection */}
      <div>
        <label className="text-sm text-white/80 mb-2 block">Tipo de Conta</label>
        <RadioGroup 
          value={role} 
          onValueChange={(value: "mentor" | "jogador") => onRoleChange(value)}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem 
              value="jogador" 
              id="jogador" 
              className="border-white/20 text-[#00B6F0]" 
            />
            <Label htmlFor="jogador" className="text-white/80 cursor-pointer">
              Jogador
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem 
              value="mentor" 
              id="mentor" 
              className="border-white/20 text-[#00B6F0]" 
            />
            <Label htmlFor="mentor" className="text-white/80 cursor-pointer">
              Mentor
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Submit Button */}
      <Button 
        type="submit" 
        className="w-full bg-[#00B6F0] hover:bg-[#0095c7] text-black font-medium py-2.5 transition-colors duration-200"
        disabled={loading || gamesLoading}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Criando conta...
          </>
        ) : (
          "Criar Conta"
        )}
      </Button>

      {/* Divider */}
      <div className="relative my-4">
        <Separator className="bg-white/10" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="bg-[#1a1a2e] px-2 text-sm text-white/60">ou</span>
        </div>
      </div>

      {/* Google Login Button */}
      <Button
        type="button"
        variant="outline"
        className="w-full border-white/10 bg-white/5 text-white hover:bg-white/10 py-2.5 transition-colors duration-200"
        onClick={onGoogleLogin}
        disabled={loading || gamesLoading}
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Continuar com Google
      </Button>
    </form>
  );
};

export default SignupForm;
